import type { CSSProperties, ForwardedRef, ReactNode } from "react";
import { type AriaMenuProps, mergeProps, type Placement } from "react-aria";
import type { MenuTriggerProps } from "react-stately";

import { useDataAttributes } from "../../hooks/useDataAttributes.ts";
import { useDOMRef } from "../../hooks/useDomRef.ts";
import { useContextualMenu } from "../../hooks/useMenu.ts";
import { forwardRef } from "../../utils.ts";
import { MenuListBox } from "../List/MenuListBox.tsx";
import { Popover } from "../Popover/Popover.tsx";
import { TriggerButtonIntent } from "../TriggerButton/settings.ts";
import { TriggerButton, type TriggerButtonProps } from "../TriggerButton/TriggerButton.tsx";

export interface MenuTriggerButtonProps2<T>
  extends AriaMenuProps<T>,
    MenuTriggerProps,
    Omit<TriggerButtonProps, "autoFocus" | "children"> {
  width?: CSSProperties["width"];
  placement?: Placement;
}

const MenuTriggerButtonRender = <T extends object, U extends object>(
  props: MenuTriggerButtonProps2<T>,
  ref: ForwardedRef<HTMLButtonElement>,
): ReactNode => {
  const { children, ...otherProps } = props;
  const triggerRef = useDOMRef(ref);
  const dataAttributes = useDataAttributes(props);
  const { state, triggerProps, menuProps } = useContextualMenu<HTMLButtonElement, U>(triggerRef, {
    ...props,
    isDisabled: !!props.isDisabled,
  });

  return (
    <>
      <TriggerButton
        {...mergeProps(otherProps, triggerProps)}
        onPress={(event) => {
          triggerProps.onPress?.(event);
          void otherProps.onPress?.(event);
        }}
        ref={triggerRef}
        intent={state.isOpen ? TriggerButtonIntent.Active : props.intent}
        {...dataAttributes}
      />
      <Popover
        state={state}
        triggerRef={triggerRef}
        placement={props.placement ?? "bottom start"}
        offset={4}
        crossOffset={props.placement?.includes("end") || props.placement?.includes("right") ? 5 : -5}
      >
        <MenuListBox
          {...mergeProps(otherProps, menuProps)}
          data-testid={`${dataAttributes["data-testid"] ?? "menu"}__popover`}
          selectionMode="none"
          style={{ width: props.width }}
        >
          {children}
        </MenuListBox>
      </Popover>
    </>
  );
};

export const MenuTriggerButton = forwardRef(MenuTriggerButtonRender);
